/**
 * Dispatch Board React Query hooks (activity-centric).
 *
 * `useDispatchBoardDay` is the single composite hook the page consumes.
 * Merges:
 *   - Crew assemblage + standalone activities   (mock store)
 *   - Real staff roster                          (useCrewMembersList)
 *   - Real tenant assets                         (getAvailableAssetsByTenant)
 *   - Real unplanned backlog                     (useUnplannedWork)
 */

import * as React from 'react';
import {
  keepPreviousData,
  useMutation,
  useQuery,
  useQueryClient,
  type QueryClient,
} from '@tanstack/react-query';

import { getLoggedinUser } from '../../../helpers/api_helper';
import { getAvailableAssetsByTenant } from '../../../services/assetService';
import { getAllClients } from '../../../services/clientService';
import { getClientSitesByClientId } from '../../../services/clientSiteService';
import { getJobsBySite } from '../../../services/jobService';
import type { ClientItem } from '../../../interfaces/Client';
import type { ClientSiteItem } from '../../../interfaces/ClientSite';
import type { JobItem } from '../../../interfaces/Job';
import { useCrewMembersList, useUnplannedWork } from '../planner-data/api';
import type { UnplannedWorkItem } from '../planner-data/types';

import { dispatchApi } from './client';
import { useRealApi } from './backend/feature-flag';
import { dispatchKeys } from './keys';
import type {
  ActivityAttachment,
  BacklogTaskCreatePayload,
  ComplianceActivityMeta,
  CaptureActivityPayload,
  CaptureDayCartageRequest,
  CrewColorToken,
  DispatchBoardDay,
  DispatchCrew,
  DispatchCrewJobActivity,
  DispatchJobType,
  DispatchVehicle,
  RollOverPayload,
  StaffMember,
  UnassignedJob,
  UnassignedKind,
} from '../types';

/* -------------------------------------------------------------------------- */
/* Mapping helpers                                                             */
/* -------------------------------------------------------------------------- */

function mapStaff(
  members: ReturnType<typeof useCrewMembersList>['data'],
  crews: DispatchCrew[],
): StaffMember[] {
  // userId → Set<crewId> they're engaged on (deduped). Engagement counts when
  // the user is EITHER a crew member OR a job-worker on any of that crew's
  // activities.
  const engagement = new Map<string, Set<string>>();
  const touch = (userId: string, crewId: string) => {
    let set = engagement.get(userId);
    if (!set) {
      set = new Set();
      engagement.set(userId, set);
    }
    set.add(crewId);
  };
  for (const c of crews) {
    for (const m of c.members) touch(m.userId, c.id);
    for (const a of c.jobs) {
      for (const w of a.workers) touch(w.userId, c.id);
    }
  }
  return (members ?? []).map((m) => ({
    userId: m.id,
    name: m.fullName,
    role: m.roleName,
    allocationCount: engagement.get(m.id)?.size ?? 0,
  }));
}

interface LegacyAsset {
  id?: string;
  assetId?: string;
  name?: string;
  assetName?: string;
  assetCode?: string;
  displayName?: string;
  code?: string;
  assetClass?: number | string;
  assetImage?: string;
  icon?: string;
  /** CartageVehicleType enum int from the tenant asset record (vehicles only). */
  vehicleType?: number;
}

function mapVehicles(
  rawAssets: LegacyAsset[],
  crews: DispatchCrew[],
): DispatchVehicle[] {
  // Per-asset map of distinct CREWS where it's engaged today. Dedupe is
  // by crew id so an asset that appears in crew A's vehicles AND on an
  // activity also in crew A only counts ONCE for that crew (mirrors the
  // staff `allocationCount` rule). It still counts twice if the same
  // asset appears on activities in different crews — which is a real
  // scheduling conflict.
  const byAsset = new Map<
    string,
    Map<string, { crewId: string; crewName: string; crewColor: CrewColorToken }>
  >();

  const addAllocation = (assetId: string, crew: DispatchCrew) => {
    if (!byAsset.has(assetId)) byAsset.set(assetId, new Map());
    // Map keyed by crewId so repeated entries collapse.
    byAsset.get(assetId)!.set(crew.id, {
      crewId: crew.id,
      crewName: crew.name,
      crewColor: crew.colorToken,
    });
  };

  for (const c of crews) {
    // 1. Crew-level vehicles
    for (const v of c.vehicles) addAllocation(v.assetId, c);
    // 2. Activity-level vehicles within this crew — the "extra
    //    vehicles" added directly to a specific activity. These count
    //    toward the same crew as the activity's host crew (no
    //    double-count when the asset also appears in crew.vehicles).
    for (const act of c.jobs) {
      if (!act.vehicles) continue;
      for (const v of act.vehicles) addAllocation(v.assetId, c);
    }
  }

  return rawAssets
    .map<DispatchVehicle>((a) => {
      const assetId = a.id ?? a.assetId ?? '';
      return {
        assetId,
        code: a.code ?? a.assetCode ?? '',
        label: a.displayName ?? a.name ?? a.assetName ?? 'Asset',
        assetClass:
          typeof a.assetClass === 'string'
            ? a.assetClass
            : typeof a.assetClass === 'number'
              ? String(a.assetClass)
              : undefined,
        imageUrl: a.assetImage ?? a.icon ?? undefined,
        allocations: Array.from(byAsset.get(assetId)?.values() ?? []),
      };
    })
    .filter((v) => v.assetId);
}

function mapTaskType(raw: number | undefined): DispatchJobType {
  switch (raw) {
    case 1: return 'ERECT';
    case 2: return 'MODIFY';
    case 3: return 'DISMANTLE';
    case 4: return 'INSPECT';
    case 5: return 'HANDOVER';
    default: return 'OTHER';
  }
}

function mapUnassigned(
  raw: UnplannedWorkItem[],
  consumedIds: Set<string>,
): UnassignedJob[] {
  return raw
    .filter((u) => !consumedIds.has(u.workOrderTaskId))
    .map((u) => ({
      kind: 'unplanned-task' as UnassignedKind,
      id: u.workOrderTaskId,
      jobId: u.jobId ?? u.jobNumber,
      jobNumber: u.jobNumber,
      jobName: u.jobName ?? '',
      title:
        u.workOrderTaskDescription?.trim() ||
        u.jobName ||
        'Untitled task',
      type: mapTaskType(
        typeof u.taskType === 'number' ? u.taskType : undefined,
      ),
      taskType:
        typeof u.taskType === 'number' ? u.taskType : undefined,
      clientName: u.clientName ?? '',
      siteName: u.siteName ?? '',
      scaffoldCode: u.scaffoldCode,
    }));
}

function computeProgress(
  crews: DispatchCrew[],
  standalone: DispatchCrewJobActivity[],
  unassignedTasks: UnassignedJob[],
): { totalToDo: number; assigned: number; percent: number } {
  const onCrews = crews.reduce((acc, c) => acc + c.jobs.length, 0);
  // Pending = standalone activities + raw unassigned tasks that haven't
  // even been wrapped in an activity yet.
  const totalToDo = onCrews + standalone.length + unassignedTasks.length;
  return {
    totalToDo,
    assigned: onCrews,
    percent: totalToDo === 0 ? 100 : Math.round((onCrews / totalToDo) * 100),
  };
}

/* -------------------------------------------------------------------------- */
/* Lookup hooks — clients + sites (drive the New Activity popup dropdowns)    */
/* -------------------------------------------------------------------------- */

/**
 * Returns every tenant client (single page, generous pageSize). Used by the
 * New Activity popup's Client dropdown so the dispatcher can schedule work
 * for ANY client, not just ones with unplanned tasks.
 */
export function useAllClients() {
  return useQuery<ClientItem[]>({
    queryKey: ['dispatch-board', 'all-clients'],
    staleTime: 5 * 60 * 1000,
    // Don't retry forever on hard failures — surface the error to the UI fast.
    retry: 1,
    queryFn: async () => {
      try {
        // pageSize=200 matches the legacy planner activity-form-modal call.
        // Backend's ModelState caps pageSize (likely [Range(1, 200)]) —
        // passing 500 returns "One or more validation errors occurred."
        const res = await getAllClients(1, 200);
        if (!Array.isArray(res?.items)) {
          console.warn('[dispatch-board] getAllClients returned unexpected shape', res);
          return [];
        }
        return res.items;
      } catch (e) {
        console.error('[dispatch-board] getAllClients failed:', e);
        throw e;
      }
    },
  });
}

/**
 * Sites for ONE client. Disabled when no clientId is provided so the dropdown
 * doesn't fire a wildcard request before the user picks a client.
 */
export function useClientSites(clientId: string | null | undefined) {
  return useQuery<ClientSiteItem[]>({
    queryKey: ['dispatch-board', 'client-sites', clientId ?? ''],
    enabled: Boolean(clientId),
    staleTime: 5 * 60 * 1000,
    retry: 1,
    queryFn: async () => {
      if (!clientId) return [];
      try {
        // pageSize=200 — see useAllClients comment for backend cap rationale.
        const res = await getClientSitesByClientId(clientId, 1, 200);
        if (!Array.isArray(res?.items)) {
          console.warn('[dispatch-board] getClientSitesByClientId returned unexpected shape', res);
          return [];
        }
        return res.items;
      } catch (e) {
        console.error('[dispatch-board] getClientSitesByClientId failed:', e);
        throw e;
      }
    },
  });
}

/**
 * Jobs for ONE site. Disabled until a siteId is provided so the dropdown
 * doesn't fire a wildcard request before the user picks a site.
 */
export function useJobsBySite(siteId: string | null | undefined) {
  return useQuery<JobItem[]>({
    queryKey: ['dispatch-board', 'jobs-by-site', siteId ?? ''],
    enabled: Boolean(siteId),
    staleTime: 5 * 60 * 1000,
    retry: 1,
    queryFn: async () => {
      if (!siteId) return [];
      try {
        // pageSize=200 — same backend cap rationale as useAllClients.
        const res = await getJobsBySite(siteId, 1, 200);
        if (!Array.isArray(res?.items)) {
          console.warn('[dispatch-board] getJobsBySite returned unexpected shape', res);
          return [];
        }
        return res.items;
      } catch (e) {
        console.error('[dispatch-board] getJobsBySite failed:', e);
        throw e;
      }
    },
  });
}

/* -------------------------------------------------------------------------- */
/* Tenant vehicles — wraps the legacy service                                  */
/* -------------------------------------------------------------------------- */

function useTenantVehicles() {
  const tenantId = React.useMemo(() => getLoggedinUser()?.tenantId, []);
  return useQuery<LegacyAsset[]>({
    queryKey: ['dispatch-board', 'tenant-assets', tenantId ?? ''],
    enabled: Boolean(tenantId),
    staleTime: 5 * 60 * 1000,
    queryFn: async () => {
      if (!tenantId) return [];
      const res = (await getAvailableAssetsByTenant(tenantId)) as unknown;
      return Array.isArray(res) ? (res as LegacyAsset[]) : [];
    },
  });
}

/* -------------------------------------------------------------------------- */
/* Composite day hook                                                          */
/* -------------------------------------------------------------------------- */

export function useDispatchBoardDay(date: string) {
  // Capture once per render so the result is stable across the hook body.
  // useRealApi() reads localStorage + import.meta.env synchronously — fine
  // to call here. Toggling the flag mid-session requires a refresh
  // (documented behaviour, not a bug).
  const realApi = useRealApi();

  const dayQuery = useQuery({
    queryKey: dispatchKeys.day(date),
    queryFn: () => dispatchApi.getDay(date),
    staleTime: 10_000,
    // Keep the previous date's data visible while the new day is
    // loading. Without this, switching dates flashes a brief blank
    // skeleton between the old and new data — dispatchers flagged it
    // as a "flicker" on prev/next clicks. With keepPreviousData, the
    // board stays populated until the new fetch lands; the toolbar
    // shows a small spinner via `isFetching` to signal the transition.
    placeholderData: keepPreviousData,
  });
  const staffQuery = useCrewMembersList();
  const vehiclesQuery = useTenantVehicles();
  // Suppress the legacy unplanned fetch in real-API mode — backend's
  // GetDay composite already returns the pre-filtered list. Saves one
  // HTTP per day load.
  const unplannedQuery = useUnplannedWork({}, { enabled: !realApi });

  const data = React.useMemo<DispatchBoardDay | undefined>(() => {
    if (!dayQuery.data) return undefined;

    // ── Lookup maps for back-filling fields the dispatch-board read
    //    doesn't carry but the UI needs (asset icons, member roles).
    //    Computed once per render; small data sets (<200 each).
    const assetById = new Map<string, LegacyAsset>();
    for (const a of vehiclesQuery.data ?? []) {
      const id = a.id ?? a.assetId;
      if (id) assetById.set(id, a);
    }
    const enrichVehicle = (v: DispatchVehicle): DispatchVehicle => {
      // Already fully populated? Pass through (mock mode supplies these
      // inline). `vehicleType` is included so the crew Shared-Cartage modal
      // can default each vehicle's type dropdown from the asset record.
      if (
        v.assetClass !== undefined &&
        v.imageUrl !== undefined &&
        v.vehicleType !== undefined
      ) {
        return v;
      }
      const src = assetById.get(v.assetId);
      if (!src) return v;
      return {
        ...v,
        assetClass:
          v.assetClass ??
          (typeof src.assetClass === 'string'
            ? src.assetClass
            : typeof src.assetClass === 'number'
              ? String(src.assetClass)
              : undefined),
        imageUrl: v.imageUrl ?? src.assetImage ?? src.icon ?? undefined,
        vehicleType:
          v.vehicleType ??
          (typeof src.vehicleType === 'number' ? src.vehicleType : undefined),
      };
    };

    const roleByUserId = new Map<string, string | undefined>();
    for (const m of staffQuery.data ?? []) {
      roleByUserId.set(m.id, m.roleName);
    }

    // Enrich crews in place: members get role, vehicles get icon class.
    // Activities on each crew get their vehicles enriched too — same
    // tenant asset pool.
    const crews = dayQuery.data.crews.map<DispatchCrew>((c) => ({
      ...c,
      members: c.members.map((m) => ({
        ...m,
        role: m.role ?? roleByUserId.get(m.userId),
      })),
      vehicles: c.vehicles.map(enrichVehicle),
      jobs: c.jobs.map((a) => ({
        ...a,
        vehicles: a.vehicles?.map(enrichVehicle),
      })),
    }));

    // Defensive date filter — the mock store is already date-keyed, but
    // (a) when the real API takes over in S2 it may not be, and
    // (b) once we allow editing an activity's date, an activity whose date
    //     drifts to another day must not linger in the wrong day's rail.
    // We compare the `YYYY-MM-DD` prefix of scheduledStart; activities
    // missing scheduledStart pass through (shouldn't happen, defensive).
    const standaloneActivities = dayQuery.data.standaloneActivities
      .filter((a) => !a.scheduledStart || a.scheduledStart.slice(0, 10) === date)
      .map((a) => ({ ...a, vehicles: a.vehicles?.map(enrichVehicle) }));
    const consumed = new Set(dayQuery.data.consumedUnassignedIds);
    const staff = mapStaff(staffQuery.data, crews);
    const assets = mapVehicles(vehiclesQuery.data ?? [], crews);
    // Phase 7b: in real-API mode the backend returns a pre-filtered
    // unplanned list (applies the "never Completed" visibility rule
    // server-side). Prefer that when present; in mock-store mode it's
    // absent and we keep the legacy useUnplannedWork + consumed filter.
    //
    // We STILL apply `consumed` even in real-API mode — the server's list
    // is authoritative only at request time. Between user action (e.g.
    // "create activity from this task") and server settle, the optimistic
    // patch has added the task's id to `consumedUnassignedIds` but the
    // backend's list hasn't refetched yet. Without this filter the dragged
    // task would briefly appear in BOTH the activity and the rail.
    const baseUnassigned = dayQuery.data.unplannedTasksFromBackend
      ? dayQuery.data.unplannedTasksFromBackend.filter((u) => !consumed.has(u.id))
      : mapUnassigned(unplannedQuery.data ?? [], consumed);
    // Sprint A: merge manually-created backlog tasks (mock mode) on top,
    // filtered by the same consumed-ids rule so a scheduled one disappears.
    // `backlogTasks` is undefined in real-API mode (backend's unplanned
    // list already includes manual WorkOrderTasks), so this is a no-op there.
    const backlog = (dayQuery.data.backlogTasks ?? []).filter(
      (u) => !consumed.has(u.id),
    );
    const unassigned = [...backlog, ...baseUnassigned];
    const progress = computeProgress(crews, standaloneActivities, unassigned);
    return {
      date,
      crews,
      standaloneActivities,
      staff,
      assets,
      unassigned,
      progress,
    };
  }, [
    dayQuery.data,
    staffQuery.data,
    vehiclesQuery.data,
    unplannedQuery.data,
    date,
  ]);

  return {
    data,
    isLoading:
      dayQuery.isLoading ||
      staffQuery.isLoading ||
      vehiclesQuery.isLoading ||
      unplannedQuery.isLoading,
    /**
     * True while ANY of the underlying queries is in flight — including
     * background revalidation when `keepPreviousData` is showing the
     * previous day. The toolbar uses this to render a small spinner
     * next to the date pill on prev/next so the dispatcher gets visual
     * feedback that new data is coming, without the board flashing
     * blank between dates.
     */
    isFetching:
      dayQuery.isFetching ||
      staffQuery.isFetching ||
      vehiclesQuery.isFetching ||
      unplannedQuery.isFetching,
    /**
     * True when react-query is serving the PREVIOUS day's data because
     * the new day is still loading. Driven by the `placeholderData:
     * keepPreviousData` option. The page uses this to dim the board
     * subtly during cross-date transitions.
     */
    isPreviousData: dayQuery.isPlaceholderData,
    isError:
      dayQuery.isError ||
      staffQuery.isError ||
      vehiclesQuery.isError ||
      unplannedQuery.isError,
    refetch: () => {
      dayQuery.refetch();
      staffQuery.refetch();
      vehiclesQuery.refetch();
      unplannedQuery.refetch();
    },
  };
}

/* -------------------------------------------------------------------------- */
/* Optimistic-mutation helper                                                  */
/* -------------------------------------------------------------------------- */

interface DayCacheShape {
  crews: DispatchCrew[];
  standaloneActivities: DispatchCrewJobActivity[];
  consumedUnassignedIds: string[];
  /** Mock-mode manually-created backlog tasks (Sprint A). */
  backlogTasks?: UnassignedJob[];
}

interface OptimisticConfig<TArgs> {
  date: string;
  patch: (state: DayCacheShape, args: TArgs) => void;
  fire: (args: TArgs) => Promise<unknown>;
}

function useOptimisticMutation<TArgs>(
  qc: QueryClient,
  cfg: OptimisticConfig<TArgs>,
) {
  return useMutation({
    mutationFn: cfg.fire,
    onMutate: async (args) => {
      const key = dispatchKeys.day(cfg.date);
      await qc.cancelQueries({ queryKey: key });
      const prev = qc.getQueryData<DayCacheShape>(key);
      if (prev) {
        const next = structuredClone(prev);
        cfg.patch(next, args);
        qc.setQueryData(key, next);
      }
      return { prev };
    },
    onError: (_e, _v, ctx) => {
      if (ctx?.prev) qc.setQueryData(dispatchKeys.day(cfg.date), ctx.prev);
    },
    onSettled: () => {
      qc.invalidateQueries({ queryKey: dispatchKeys.day(cfg.date) });
    },
  });
}

/* -------------------------------------------------------------------------- */
/* Activity-shape helpers                                                      */
/* -------------------------------------------------------------------------- */

function findActivityInState(
  state: DayCacheShape,
  activityId: string,
): DispatchCrewJobActivity | undefined {
  for (const c of state.crews) {
    const a = c.jobs.find((j) => j.activityId === activityId);
    if (a) return a;
  }
  return state.standaloneActivities.find((a) => a.activityId === activityId);
}

function makeActivityShell(
  date: string,
  payload: {
    clientId: string;
    clientName: string;
    siteId: string;
    siteName: string;
    jobId?: string;
    jobNumber?: string;
    jobChargeUpType?: number;
    siteTypeColorHex?: string;
    title: string;
    type: DispatchJobType;
    notes?: string;
    initialTasks?: UnassignedJob[];
    attachments?: ActivityAttachment[];
    isUnplanned?: boolean;
    unplannedDescription?: string;
    isCompliance?: boolean;
    compliance?: ComplianceActivityMeta;
  },
): DispatchCrewJobActivity {
  const tasks = payload.initialTasks ?? [];
  // JOB-INHERIT — mirror mock-store's createActivity rule. When the
  // popup picked tasks but skipped the Job, the optimistic shell still
  // shows the inherited Job (from the first task) so the UNPLANNED
  // badge doesn't briefly flash before settling.
  const inheritedJobId = payload.jobId ?? tasks[0]?.jobId;
  const inheritedJobNumber = payload.jobNumber ?? tasks[0]?.jobNumber;
  return {
    activityId: `act-${Date.now()}-${Math.random().toString(36).slice(2, 6)}`,
    sourceTaskId: tasks[0]?.id,
    jobNumber: 'ACT…',                  // server-numbered; mock store will replace on refetch
    parentJobId: inheritedJobId,
    parentJobNumber: inheritedJobNumber,
    parentJobChargeUpType: payload.jobChargeUpType,
    siteTypeColorHex: payload.siteTypeColorHex,
    title: payload.title || 'New activity',
    type: payload.type,
    status: 'Scheduled',
    scheduledStart: `${date}T08:00`,
    scheduledEnd: `${date}T17:00`,
    position: 0,
    clientId: payload.clientId,
    clientName: payload.clientName,
    siteId: payload.siteId,
    siteName: payload.siteName,
    notes: payload.notes?.trim() || undefined,
    isUnplanned: payload.isUnplanned || undefined,
    unplannedDescription: payload.unplannedDescription?.trim() || undefined,
    isCompliance: payload.isCompliance || undefined,
    compliance: payload.compliance,
    tasks: tasks.map((t, i) => ({
      taskId: `t-${Date.now()}-${i}`,
      workOrderTaskId: t.id,
      jobNumber: t.jobNumber,
      jobName: t.jobName,
      description: t.title,
      type: t.type,
      taskTypeRaw: t.taskType,
      scaffoldCode: t.scaffoldCode,
      clientName: t.clientName,
      siteName: t.siteName,
      // Mirror mapUnassignedToTask — keep a board-created task's charge
      // override + billable flag in the optimistic create shell.
      chargeUpType: t.chargeUpType,
      isBillable: t.isBillable,
    })),
    workers: [],
    // Carry attachments through the optimistic shell so the paperclip
    // badge appears immediately (before the server settle round-trip).
    attachments:
      payload.attachments && payload.attachments.length > 0
        ? payload.attachments
        : undefined,
  };
}

/* -------------------------------------------------------------------------- */
/* Mutations                                                                   */
/* -------------------------------------------------------------------------- */

// Color rotation matching the mock-store's createCrew default. Kept here
// (not in mock-store) so real-API mode gets the same defaulting behaviour
// without needing the mock-store loaded at all (Phase 7i cleanup).
const CREW_COLOR_ROTATION: readonly CrewColorToken[] = [
  'sky', 'emerald', 'amber', 'rose', 'violet', 'slate',
];

export function useCreateCrew(date: string) {
  const qc = useQueryClient();
  return useOptimisticMutation<{ name?: string; colorToken?: CrewColorToken }>(
    qc,
    {
      date,
      patch: () => { /* refetch on settle */ },
      fire: (args) => {
        // Pre-compute "Crew N" + color rotation from cache so real-API
        // mode produces the same defaults as mock-store (which does this
        // internally). Backend has no auto-naming; the FE owns it.
        const cached = qc.getQueryData<DayCacheShape>(dispatchKeys.day(date));
        const existing = cached?.crews ?? [];

        // Auto-number: lowest "Crew N" not currently used on this day.
        let name = args.name?.trim() || '';
        if (!name) {
          const used = new Set(
            existing
              .map((c) => /^Crew\s+(\d+)$/.exec(c.name)?.[1])
              .filter(Boolean)
              .map(Number),
          );
          let n = 1;
          while (used.has(n)) n++;
          name = `Crew ${n}`;
        }

        // Color rotation: pure modulo by crew count — matches mock-store
        // exactly (`colors[day.crews.length % colors.length]` at
        // mock-store.ts:291). Don't second-guess with "first unused" —
        // that diverges from mock and causes confusing color shuffles on
        // crew create/delete.
        const colorToken =
          args.colorToken ??
          CREW_COLOR_ROTATION[existing.length % CREW_COLOR_ROTATION.length];

        return dispatchApi.createCrew(date, { name, colorToken });
      },
    },
  );
}

export function useDeleteCrew(date: string) {
  const qc = useQueryClient();
  return useOptimisticMutation<{ crewId: string }>(qc, {
    date,
    patch: (state, { crewId }) => {
      const crew = state.crews.find((c) => c.id === crewId);
      if (!crew) return;
      // Cascade activities back to standalone
      for (const act of crew.jobs) {
        state.standaloneActivities.push(act);
      }
      state.crews = state.crews.filter((c) => c.id !== crewId);
    },
    fire: ({ crewId }) => dispatchApi.deleteCrew(date, crewId),
  });
}

export function useRenameCrew(date: string) {
  const qc = useQueryClient();
  return useOptimisticMutation<{ crewId: string; name: string }>(qc, {
    date,
    patch: (state, { crewId, name }) => {
      const c = state.crews.find((x) => x.id === crewId);
      if (c) c.name = name;
    },
    fire: ({ crewId, name }) => {
      // Pluck the current rowVersion from cache so the backend can detect
      // concurrent edits (Phase 7c). Undefined in mock-mode → backend
      // skips the optimistic-concurrency check.
      const cached = qc.getQueryData<DayCacheShape>(dispatchKeys.day(date));
      const rowVersion = cached?.crews.find((c) => c.id === crewId)?.rowVersion;
      return dispatchApi.renameCrew(date, crewId, name, { rowVersion });
    },
  });
}

export function useSetCrewColor(date: string) {
  const qc = useQueryClient();
  return useOptimisticMutation<{
    crewId: string;
    colorToken: CrewColorToken;
  }>(qc, {
    date,
    patch: (state, { crewId, colorToken }) => {
      const c = state.crews.find((x) => x.id === crewId);
      if (c) c.colorToken = colorToken;
    },
    fire: ({ crewId, colorToken }) => {
      const cached = qc.getQueryData<DayCacheShape>(dispatchKeys.day(date));
      const rowVersion = cached?.crews.find((c) => c.id === crewId)?.rowVersion;
      return dispatchApi.setCrewColor(date, crewId, colorToken, { rowVersion });
    },
  });
}

export function useAddMember(date: string) {
  const qc = useQueryClient();
  return useOptimisticMutation<{
    crewId: string;
    member: { userId: string; name: string; role?: string };
  }>(qc, {
    date,
    patch: (state, { crewId, member }) => {
      const crew = state.crews.find((c) => c.id === crewId);
      if (!crew || crew.members.some((m) => m.userId === member.userId)) return;
      crew.members.push({
        userId: member.userId,
        name: member.name,
        role: member.role,
        isLead: crew.members.length === 0,
      });
      if (crew.members.length === 1) crew.leadUserId = member.userId;
    },
    fire: ({ crewId, member }) => {
      // First-member-becomes-lead parity: if the destination crew has
      // zero members at fire time, pass isLead=true so the backend
      // promotes them in the same atomic transaction (mirrors the
      // optimistic patch above). Avoids a second round-trip to SetLead.
      const cached = qc.getQueryData<DayCacheShape>(dispatchKeys.day(date));
      const crew = cached?.crews.find((c) => c.id === crewId);
      const isLead = !crew || crew.members.length === 0;
      return dispatchApi.addMember(date, crewId, member, { isLead });
    },
  });
}

export function useRemoveMember(date: string) {
  const qc = useQueryClient();
  return useOptimisticMutation<{ crewId: string; userId: string }>(qc, {
    date,
    patch: (state, { crewId, userId }) => {
      const crew = state.crews.find((c) => c.id === crewId);
      if (!crew) return;
      const wasLead = crew.leadUserId === userId;
      crew.members = crew.members.filter((m) => m.userId !== userId);
      if (wasLead) {
        crew.leadUserId = crew.members[0]?.userId ?? null;
        if (crew.members[0]) crew.members[0].isLead = true;
      }
    },
    fire: ({ crewId, userId }) => {
      // Backend delete keys off DbCrewMember.Id (the join-row id), not
      // userId. Look it up from the cached crew so we don't need a
      // separate GetMembers round-trip.
      const cached = qc.getQueryData<DayCacheShape>(dispatchKeys.day(date));
      const memberRowId = cached?.crews
        .find((c) => c.id === crewId)
        ?.members.find((m) => m.userId === userId)?.memberRowId;
      return dispatchApi.removeMember(date, crewId, userId, { memberRowId });
    },
  });
}

export function useSetLead(date: string) {
  const qc = useQueryClient();
  return useOptimisticMutation<{ crewId: string; userId: string }>(qc, {
    date,
    patch: (state, { crewId, userId }) => {
      const crew = state.crews.find((c) => c.id === crewId);
      if (!crew) return;
      crew.members = crew.members.map((m) => ({
        ...m,
        isLead: m.userId === userId,
      }));
      crew.leadUserId = userId;
    },
    fire: ({ crewId, userId }) => dispatchApi.setLead(date, crewId, userId),
  });
}

export function useAddVehicle(date: string) {
  const qc = useQueryClient();
  return useOptimisticMutation<{ crewId: string; vehicle: DispatchVehicle }>(
    qc,
    {
      date,
      patch: (state, { crewId, vehicle }) => {
        const dest = state.crews.find((c) => c.id === crewId);
        if (!dest) return;
        if (dest.vehicles.some((v) => v.assetId === vehicle.assetId)) return;
        dest.vehicles.push(vehicle);
      },
      fire: ({ crewId, vehicle }) =>
        dispatchApi.addVehicle(date, crewId, vehicle),
    },
  );
}

export function useRemoveVehicle(date: string) {
  const qc = useQueryClient();
  return useOptimisticMutation<{ crewId: string; assetId: string }>(qc, {
    date,
    patch: (state, { crewId, assetId }) => {
      const crew = state.crews.find((c) => c.id === crewId);
      if (!crew) return;
      crew.vehicles = crew.vehicles.filter((v) => v.assetId !== assetId);
    },
    fire: ({ crewId, assetId }) => {
      // Backend delete keys off DbCrewAsset.Id (the join-row id), not
      // the tenant asset id. Pull it from the cached vehicle entry.
      const cached = qc.getQueryData<DayCacheShape>(dispatchKeys.day(date));
      const rowId = cached?.crews
        .find((c) => c.id === crewId)
        ?.vehicles.find((v) => v.assetId === assetId)?.rowId;
      return dispatchApi.removeVehicle(date, crewId, assetId, { rowId });
    },
  });
}

/* ---------- Activity lifecycle ---------- */

export function useCreateActivity(date: string) {
  const qc = useQueryClient();
  return useOptimisticMutation<{
    clientId: string;
    clientName: string;
    siteId: string;
    siteName: string;
    jobId?: string;
    jobNumber?: string;
    jobChargeUpType?: number;
    siteTypeColorHex?: string;
    title: string;
    type: DispatchJobType;
    crewId?: string;
    notes?: string;
    initialTasks?: UnassignedJob[];
    /** Files + photos attached at creation. */
    attachments?: ActivityAttachment[];
    /**
     * Target date for the new activity. Defaults to the hook's bound date
     * (= the board's currently displayed day). Override this when the
     * popup's date picker lands on a different day — the activity then
     * lands in THAT day's bucket, not today's.
     */
    date?: string;
    /** Unplanned (phone-call) work flag — see DispatchCrewJobActivity. */
    isUnplanned?: boolean;
    /** Description of the unplanned work — required when isUnplanned. */
    unplannedDescription?: string;
    /** Compliance-check flag + payload (mock prototype). */
    isCompliance?: boolean;
    compliance?: ComplianceActivityMeta;
  }>(qc, {
    date,
    patch: (state, args) => {
      // When creating for a DIFFERENT day, the current day's cache should
      // NOT receive an optimistic update — the new activity won't appear
      // on this view. Skip the patch and rely on the target-date cache
      // invalidation (in `fire`) to refetch fresh when the user navigates.
      const targetDate = args.date ?? date;
      if (targetDate !== date) return;
      const activity = makeActivityShell(targetDate, args);
      if (args.crewId) {
        const crew = state.crews.find((c) => c.id === args.crewId);
        if (crew) {
          // Mirror the mock-store auto-assign rule: every current crew
          // member becomes a worker on the new activity. Keeps optimistic
          // UI in sync — workers show up immediately without waiting for
          // the mutation to settle.
          activity.workers = crew.members.map((m) => ({
            userId: m.userId,
            name: m.name,
          }));
          // Same default for vehicles — copy the crew's gear into the
          // activity. Stays in sync with the mock-store rule so the
          // optimistic shell renders identically to the settled one.
          activity.vehicles = crew.vehicles.map((v) => ({
            assetId: v.assetId,
            code: v.code,
            label: v.label,
            assetClass: v.assetClass,
            imageUrl: v.imageUrl,
          }));
          activity.position = crew.jobs.length;
          crew.jobs.push(activity);
        } else {
          state.standaloneActivities.push(activity);
        }
      } else {
        state.standaloneActivities.push(activity);
      }
      for (const t of args.initialTasks ?? []) {
        if (!state.consumedUnassignedIds.includes(t.id)) {
          state.consumedUnassignedIds.push(t.id);
        }
      }
    },
    fire: async (args) => {
      const targetDate = args.date ?? date;
      await dispatchApi.createActivity(targetDate, args);
      // If we wrote to a DIFFERENT day, invalidate that day's cache so
      // the new activity surfaces whenever the user navigates there.
      if (targetDate !== date) {
        qc.invalidateQueries({ queryKey: dispatchKeys.day(targetDate) });
      }
    },
  });
}

export function useAssignActivityToCrew(date: string) {
  const qc = useQueryClient();
  return useOptimisticMutation<{ activityId: string; crewId: string }>(qc, {
    date,
    patch: (state, { activityId, crewId }) => {
      const idx = state.standaloneActivities.findIndex(
        (a) => a.activityId === activityId,
      );
      if (idx < 0) return;
      const dest = state.crews.find((c) => c.id === crewId);
      if (!dest) return;
      const activity = state.standaloneActivities[idx];
      state.standaloneActivities.splice(idx, 1);
      activity.position = dest.jobs.length;
      // REPLACE workers with destination crew's members (matches mock-store).
      // Previously this was a merge but dispatchers flagged it as buggy —
      // an activity arriving on a crew should show only that crew's members.
      activity.workers = dest.members.map((m) => ({
        userId: m.userId,
        name: m.name,
      }));
      // Same REPLACE rule for vehicles — the activity inherits the
      // destination crew's vehicle pool when it arrives. Standalone
      // activities had no host crew, so any extra vehicles added while
      // standalone get dropped (consistent with the worker rule).
      activity.vehicles = dest.vehicles.map((v) => ({
        assetId: v.assetId,
        code: v.code,
        label: v.label,
        assetClass: v.assetClass,
        imageUrl: v.imageUrl,
      }));
      dest.jobs.push(activity);
    },
    fire: ({ activityId, crewId }) =>
      dispatchApi.assignActivityToCrew(date, activityId, crewId),
  });
}

export function useUpdateActivity(date: string) {
  const qc = useQueryClient();
  return useOptimisticMutation<{
    activityId: string;
    title?: string;
    notes?: string;
    jobId?: string | null;
    jobNumber?: string | null;
    jobChargeUpType?: number | null;
    /** New date (YYYY-MM-DD) — moves a standalone activity to another day. */
    newDate?: string;
    initialTasks?: UnassignedJob[];
    /**
     * Full desired attachment list. REPLACES the existing one — `undefined`
     * leaves attachments untouched, `[]` clears all.
     */
    attachments?: ActivityAttachment[];
    /** Refine the unplanned-work description (no-op for planned activities). */
    unplannedDescription?: string;
  }>(qc, {
    date,
    patch: (state, args) => {
      const activity = findActivityInState(state, args.activityId);
      if (!activity) return;
      // Date change → optimistically REMOVE from current day's cache.
      // The activity will reappear once the new date's cache refetches
      // (onSettled invalidates everything via the helper's settle hook).
      // We don't try to inject into the new day's cache here because it
      // may not be loaded; cleaner to let it refetch fresh.
      if (
        args.newDate &&
        args.newDate !== date &&
        state.standaloneActivities.some((a) => a.activityId === args.activityId)
      ) {
        state.standaloneActivities = state.standaloneActivities.filter(
          (a) => a.activityId !== args.activityId,
        );
        for (const t of activity.tasks) {
          state.consumedUnassignedIds = state.consumedUnassignedIds.filter(
            (id) => id !== t.workOrderTaskId,
          );
        }
        return; // skip in-place patches — the activity is gone from this day
      }
      if (args.title !== undefined) activity.title = args.title;
      if (args.notes !== undefined) {
        activity.notes = args.notes.trim() || undefined;
      }
      if (args.jobId !== undefined) {
        activity.parentJobId = args.jobId || undefined;
      }
      if (args.jobNumber !== undefined) {
        activity.parentJobNumber = args.jobNumber || undefined;
      }
      if (args.jobChargeUpType !== undefined) {
        activity.parentJobChargeUpType = args.jobChargeUpType ?? undefined;
      }
      if (args.unplannedDescription !== undefined) {
        activity.unplannedDescription =
          args.unplannedDescription.trim() || undefined;
      }
      // NOTE — tasks + attachments are DELIBERATELY NOT patched optimistically
      // here. `fire` (below) computes the task/attachment DIFF by reading the
      // activity's CURRENT tasks/attachments from this same cache. If we wrote
      // the desired tasks/attachments in now, `fire` would read them back as
      // "already current" → the diff would find nothing to add → no POST
      // /tasks and no attachment upload would fire (the real-API "task /
      // attachment not saving on edit" bug). Leaving the cache's tasks /
      // attachments at their pre-edit values keeps the diff baseline honest;
      // the settle refetch (onSettled) reflects the saved result a moment
      // later. Scalar fields above stay optimistic — they ride the PUT body,
      // not a diff, so they can't pollute it. (`args.initialTasks` /
      // `args.attachments` are still forwarded to the diff by `fire` below.)
    },
    fire: async ({ activityId, ...patch }) => {
      // Pull rowVersion + diffing state from cache so client.ts can do the
      // task/attachment diff without an extra GET round-trip. Mock mode
      // ignores these; only the real-API path consumes them.
      //
      // `cacheHit` tells client.ts whether the empty maps mean "activity
      // legitimately has nothing" (cacheHit=true) vs "we couldn't read
      // cache, please fetch fresh" (cacheHit=false). Without this flag
      // an empty currentTaskRowIds would be misread on cache miss and
      // the diff would duplicate every desired task.
      const cached = qc.getQueryData<DayCacheShape>(dispatchKeys.day(date));
      const cachedActivity = cached ? findActivityInState(cached, activityId) : undefined;
      const cacheHit = cachedActivity !== undefined;
      const currentTaskRowIds: Record<string, string> = {};
      for (const t of cachedActivity?.tasks ?? []) {
        currentTaskRowIds[t.workOrderTaskId] = t.taskId;
      }
      const currentAttachments = (cachedActivity?.attachments ?? []).map((a) => ({
        id: a.id,
        name: a.name,
      }));
      await dispatchApi.updateActivity(date, activityId, patch, {
        rowVersion: undefined, // DispatchActivity doesn't surface rowVersion to FE types yet (defer to Phase 7d.next)
        currentTaskRowIds,
        currentAttachments,
        cacheHit,
      });
      // If the activity was moved to a different day, the destination
      // day's cache is now stale (it gained an activity). Invalidate it
      // so the new day refetches fresh whenever the user navigates there.
      if (patch.newDate && patch.newDate !== date) {
        qc.invalidateQueries({ queryKey: dispatchKeys.day(patch.newDate) });
      }
    },
  });
}

export function useDeleteActivity(date: string) {
  const qc = useQueryClient();
  return useOptimisticMutation<{ activityId: string }>(qc, {
    date,
    patch: (state, { activityId }) => {
      // Release linked tasks back to Unassigned, then drop the activity
      // from whichever list holds it (crew jobs OR standalone).
      const releaseTasks = (act: DispatchCrewJobActivity) => {
        const taskIds = new Set(act.tasks.map((t) => t.workOrderTaskId));
        state.consumedUnassignedIds = state.consumedUnassignedIds.filter(
          (id) => !taskIds.has(id),
        );
      };
      for (const c of state.crews) {
        const idx = c.jobs.findIndex((j) => j.activityId === activityId);
        if (idx >= 0) {
          releaseTasks(c.jobs[idx]);
          c.jobs.splice(idx, 1);
          return;
        }
      }
      const sIdx = state.standaloneActivities.findIndex(
        (a) => a.activityId === activityId,
      );
      if (sIdx >= 0) {
        releaseTasks(state.standaloneActivities[sIdx]);
        state.standaloneActivities.splice(sIdx, 1);
      }
    },
    fire: ({ activityId }) => dispatchApi.deleteActivity(date, activityId),
  });
}

export function useUnassignActivityFromCrew(date: string) {
  const qc = useQueryClient();
  return useOptimisticMutation<{ activityId: string }>(qc, {
    date,
    patch: (state, { activityId }) => {
      for (const c of state.crews) {
        const idx = c.jobs.findIndex((j) => j.activityId === activityId);
        if (idx >= 0) {
          const act = c.jobs[idx];
          c.jobs.splice(idx, 1);
          state.standaloneActivities.push(act);
          return;
        }
      }
    },
    fire: ({ activityId }) =>
      dispatchApi.unassignActivityFromCrew(date, activityId),
  });
}

export function useMoveActivity(date: string) {
  const qc = useQueryClient();
  return useOptimisticMutation<{ activityId: string; toCrewId: string }>(qc, {
    date,
    patch: (state, { activityId, toCrewId }) => {
      for (const c of state.crews) {
        const idx = c.jobs.findIndex((j) => j.activityId === activityId);
        if (idx >= 0) {
          const act = c.jobs[idx];
          c.jobs.splice(idx, 1);
          const dest = state.crews.find((dc) => dc.id === toCrewId);
          if (dest) {
            act.position = dest.jobs.length;
            // REPLACE workers with destination crew's members (matches
            // mock-store). The source crew's members are dropped —
            // dispatcher feedback: keeping them around caused phantom
            // "N not in crew" warnings on every move.
            act.workers = dest.members.map((m) => ({
              userId: m.userId,
              name: m.name,
            }));
            // Same REPLACE rule for vehicles — see assign hook.
            act.vehicles = dest.vehicles.map((v) => ({
              assetId: v.assetId,
              code: v.code,
              label: v.label,
              assetClass: v.assetClass,
              imageUrl: v.imageUrl,
            }));
            dest.jobs.push(act);
          } else {
            state.standaloneActivities.push(act);
          }
          return;
        }
      }
    },
    fire: ({ activityId, toCrewId }) =>
      dispatchApi.moveActivityBetweenCrews(date, activityId, toCrewId),
  });
}

/* ---------- Task linkage ---------- */

export function useLinkTaskToActivity(date: string) {
  const qc = useQueryClient();
  return useOptimisticMutation<{ activityId: string; task: UnassignedJob }>(
    qc,
    {
      date,
      patch: (state, { activityId, task }) => {
        const activity = findActivityInState(state, activityId);
        if (!activity) return;
        if (state.consumedUnassignedIds.includes(task.id)) return;
        // JOB-MATCH GUARD — keep optimistic state in sync with the store
        // rule. Without this, the UI would flash a linked task that then
        // disappears on settle.
        if (
          activity.parentJobNumber &&
          task.jobNumber !== activity.parentJobNumber
        ) {
          return;
        }
        // JOB-INHERIT — mirror the mock-store rule. When the activity
        // has no parent Job (unplanned), the first task linked pins the
        // activity to that task's Job. UNPLANNED badge disappears the
        // instant the optimistic patch lands (no waiting for settle).
        if (!activity.parentJobNumber && task.jobNumber) {
          activity.parentJobId = task.jobId || activity.parentJobId;
          activity.parentJobNumber = task.jobNumber;
        }
        activity.tasks.push({
          taskId: `t-${Date.now()}`,
          workOrderTaskId: task.id,
          jobNumber: task.jobNumber,
          jobName: task.jobName,
          description: task.title,
          type: task.type,
          taskTypeRaw: task.taskType,
          scaffoldCode: task.scaffoldCode,
          clientName: task.clientName,
          siteName: task.siteName,
          // Mirror mapUnassignedToTask — preserve a board-created task's
          // charge override + billable flag through the optimistic patch so
          // the instant UI matches what the store settles to.
          chargeUpType: task.chargeUpType,
          isBillable: task.isBillable,
        });
        state.consumedUnassignedIds.push(task.id);
      },
      fire: ({ activityId, task }) =>
        dispatchApi.linkTaskToActivity(date, activityId, task),
    },
  );
}

export function useUnlinkTaskFromActivity(date: string) {
  const qc = useQueryClient();
  return useOptimisticMutation<{ activityId: string; taskId: string }>(qc, {
    date,
    patch: (state, { activityId, taskId }) => {
      const activity = findActivityInState(state, activityId);
      if (!activity) return;
      const idx = activity.tasks.findIndex((t) => t.taskId === taskId);
      if (idx < 0) return;
      const task = activity.tasks[idx];
      activity.tasks.splice(idx, 1);
      state.consumedUnassignedIds = state.consumedUnassignedIds.filter(
        (id) => id !== task.workOrderTaskId,
      );
    },
    fire: ({ activityId, taskId }) =>
      dispatchApi.unlinkTaskFromActivity(date, activityId, taskId),
  });
}

/* ---------- Job workers ---------- */

export function useAddJobWorker(date: string) {
  const qc = useQueryClient();
  return useOptimisticMutation<{
    activityId: string;
    worker: { userId: string; name: string };
  }>(qc, {
    date,
    patch: (state, { activityId, worker }) => {
      const activity = findActivityInState(state, activityId);
      if (!activity) return;
      if (!activity.workers.some((w) => w.userId === worker.userId)) {
        activity.workers.push({ userId: worker.userId, name: worker.name });
      }
    },
    fire: ({ activityId, worker }) =>
      dispatchApi.addJobWorker(date, activityId, worker),
  });
}

export function useRemoveJobWorker(date: string) {
  const qc = useQueryClient();
  return useOptimisticMutation<{ activityId: string; userId: string }>(qc, {
    date,
    patch: (state, { activityId, userId }) => {
      const activity = findActivityInState(state, activityId);
      if (!activity) return;
      activity.workers = activity.workers.filter((w) => w.userId !== userId);
    },
    fire: ({ activityId, userId }) => {
      // Look up DbDispatchActivityWorker.Id from cache so the real-API
      // delete addresses the correct row. Defensive: client.ts falls
      // back to a GetWorkers if undefined.
      const cached = qc.getQueryData<DayCacheShape>(dispatchKeys.day(date));
      const activity = cached ? findActivityInState(cached, activityId) : undefined;
      const workerRowId = activity?.workers.find((w) => w.userId === userId)?.workerRowId;
      return dispatchApi.removeJobWorker(date, activityId, userId, { workerRowId });
    },
  });
}

/* ---------- Job vehicles ----------
 * Asset-on-activity mirror of the job-workers flow. Adds an asset to a
 * specific activity's vehicle list (separate from the crew's pool — the
 * "extra vehicle needed for the extra staff on this activity" pattern).
 */

export function useAddJobVehicle(date: string) {
  const qc = useQueryClient();
  return useOptimisticMutation<{
    activityId: string;
    vehicle: {
      assetId: string;
      code: string;
      label: string;
      assetClass?: string;
      imageUrl?: string;
    };
  }>(qc, {
    date,
    patch: (state, { activityId, vehicle }) => {
      const activity = findActivityInState(state, activityId);
      if (!activity) return;
      if (!activity.vehicles) activity.vehicles = [];
      if (activity.vehicles.some((v) => v.assetId === vehicle.assetId)) {
        return; // already on this activity — no-op
      }
      activity.vehicles.push({
        assetId: vehicle.assetId,
        code: vehicle.code,
        label: vehicle.label,
        assetClass: vehicle.assetClass,
        imageUrl: vehicle.imageUrl,
      });
    },
    fire: ({ activityId, vehicle }) =>
      dispatchApi.addJobVehicle(date, activityId, vehicle),
  });
}

export function useRemoveJobVehicle(date: string) {
  const qc = useQueryClient();
  return useOptimisticMutation<{ activityId: string; assetId: string }>(qc, {
    date,
    patch: (state, { activityId, assetId }) => {
      const activity = findActivityInState(state, activityId);
      if (!activity || !activity.vehicles) return;
      activity.vehicles = activity.vehicles.filter(
        (v) => v.assetId !== assetId,
      );
      if (activity.vehicles.length === 0) activity.vehicles = undefined;
    },
    fire: ({ activityId, assetId }) => {
      // Look up DbDispatchActivityAsset.Id from cache (mapper populates
      // `rowId` on activity-nested vehicles). Defensive fallback in
      // client.ts via GetAssets if cache miss.
      const cached = qc.getQueryData<DayCacheShape>(dispatchKeys.day(date));
      const activity = cached ? findActivityInState(cached, activityId) : undefined;
      const rowId = activity?.vehicles?.find((v) => v.assetId === assetId)?.rowId;
      return dispatchApi.removeJobVehicle(date, activityId, assetId, { rowId });
    },
  });
}

/* ---------- Copy Day — clone crews from another date ---------- */

/**
 * Lightweight read of just the crew assemblage for a single date —
 * powers the Copy Day modal's "preview source day" panel. Shares the
 * cache key with `useDispatchBoardDay`, so if the user picks a date
 * they've already loaded (e.g. yesterday they were just viewing), no
 * extra fetch fires.
 *
 * Disabled when `date` is falsy so the modal can render before the user
 * has picked a source date without firing a wildcard request.
 */
export function useCrewsForDay(date: string | null | undefined) {
  return useQuery({
    queryKey: dispatchKeys.day(date ?? ''),
    queryFn: () => dispatchApi.getDay(date!),
    enabled: Boolean(date),
    staleTime: 10_000,
  });
}

/**
 * Clone the chosen crews from a source date to the current board date.
 * Optimistic patch pushes the cloned shells into the target day's cache
 * immediately so the empty-board hero disappears the instant the user
 * clicks Copy — important demo polish (no flicker).
 *
 * The optimistic shells use temporary client-side ids; onSettled
 * invalidates and the authoritative server ids land on the next refetch.
 */
export function useCloneCrews(targetDate: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({
      fromDate,
      crewIds,
    }: {
      fromDate: string;
      crewIds: string[];
    }) => dispatchApi.cloneCrews(fromDate, targetDate, { crewIds }),
    onMutate: async ({ fromDate, crewIds }) => {
      const targetKey = dispatchKeys.day(targetDate);
      await qc.cancelQueries({ queryKey: targetKey });
      const prevTarget = qc.getQueryData<DayCacheShape>(targetKey);
      // Pull the source crews from cache if we have them — that's what
      // the modal just displayed, so it WILL be in cache. If we don't
      // (defensive path), skip optimistic and let invalidate refetch.
      const sourceData = qc.getQueryData<DayCacheShape>(
        dispatchKeys.day(fromDate),
      );
      if (prevTarget && sourceData) {
        const next = structuredClone(prevTarget);
        // Mode-aware crew selection. Backend's CopyDay has NO selector —
        // it clones every source crew (DispatchBoardService.CopyDay).
        // To eliminate the flicker (optimistic shows only picked → refetch
        // shows all), push every source crew in real-API mode. Mock mode
        // keeps the selective behaviour so its semantics stay intact.
        const idsToClone = useRealApi()
          ? new Set(sourceData.crews.map((c) => c.id))
          : new Set(crewIds);
        const ordered = [...sourceData.crews].sort(
          (a, b) => a.position - b.position,
        );
        let i = 0;
        for (const src of ordered) {
          if (!idsToClone.has(src.id)) continue;
          next.crews.push({
            id: `tmp-c-${Date.now()}-${i++}`,
            name: src.name,
            position: next.crews.length,
            colorToken: src.colorToken,
            leadUserId: src.leadUserId,
            // Shallow-clone arrays so the new crew is independent.
            members: src.members.map((m) => ({ ...m })),
            vehicles: src.vehicles.map((v) => ({ ...v })),
            jobs: [],
          });
        }
        qc.setQueryData(targetKey, next);
      }
      return { prevTarget };
    },
    onError: (_e, _v, ctx) => {
      if (ctx?.prevTarget) {
        qc.setQueryData(dispatchKeys.day(targetDate), ctx.prevTarget);
      }
    },
    onSettled: () => {
      qc.invalidateQueries({ queryKey: dispatchKeys.day(targetDate) });
    },
  });
}

/* ---------- Demo data reset ---------- */

/**
 * Nuclear option — wipes every crew + every activity across every date
 * and clears localStorage. Drives the toolbar's small Reset button.
 *
 * Invalidates `dispatchKeys.all` so every cached day refetches the new
 * empty state. Not optimistic (the user EXPECTS the board to clear, so
 * a brief skeleton-flash is the right feedback that "yes, it worked").
 */
export function useResetDemoData() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: () => dispatchApi.reset(),
    onSettled: () => {
      qc.invalidateQueries({ queryKey: dispatchKeys.all });
    },
  });
}

/* ---------- Capture reset (TEST/ADMIN — server gates to the reset-admin) ---------- */

/** Un-capture ONE activity → capturable again. Invalidates the day + summary. */
export function useUncaptureActivity(date: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (activityId: string) => dispatchApi.uncaptureActivity(activityId),
    onSettled: () => {
      qc.invalidateQueries({ queryKey: dispatchKeys.day(date) });
      qc.invalidateQueries({ queryKey: dispatchKeys.captureSummary(date) });
    },
  });
}

/** Reset EVERY capture on the day. Invalidates the day + summary. */
export function useResetDayCaptures(date: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: () => dispatchApi.resetDayCaptures(date),
    onSettled: () => {
      qc.invalidateQueries({ queryKey: dispatchKeys.day(date) });
      qc.invalidateQueries({ queryKey: dispatchKeys.captureSummary(date) });
    },
  });
}

/* ---------- Bulk staff removal — "mark away today" ---------- */

/**
 * One-click clean — strip a staff member from every crew member list and
 * every activity worker list on the day. Used by the Staff Rail's ⊘ button
 * for the "Sarah called in sick" scenario.
 *
 * Hand-rolled (not via `useOptimisticMutation`) because:
 *   - The mutation result carries the counts the toast needs to show
 *     ("Removed from 2 crews and 4 activities"). The shared helper has a
 *     `Promise<unknown>` return signature that loses type info.
 *   - The patch logic spans every crew + standalone — fine to inline here
 *     to keep the helper interface narrow.
 */
export function useMarkStaffAway(date: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ userId }: { userId: string; userName: string }) =>
      dispatchApi.markStaffAway(date, userId),
    onMutate: async ({ userId }) => {
      const key = dispatchKeys.day(date);
      await qc.cancelQueries({ queryKey: key });
      const prev = qc.getQueryData<DayCacheShape>(key);
      if (prev) {
        const next = structuredClone(prev);
        // Mode-aware optimistic patch. Backend (real-API) deliberately
        // preserves crew memberships and only deactivates today's
        // activity worker rows. Mock-store strips both for parity with
        // the demo's mental model. We match the impending server state
        // here so refetch doesn't flicker the crew-member chips back in
        // (real-API) or out (mock).
        const stripCrewMembers = !useRealApi();
        for (const crew of next.crews) {
          if (stripCrewMembers && crew.members.some((m) => m.userId === userId)) {
            crew.members = crew.members.filter((m) => m.userId !== userId);
            if (crew.leadUserId === userId) crew.leadUserId = null;
          }
          // Activity workers on this crew — both modes strip these.
          for (const act of crew.jobs) {
            if (act.workers.some((w) => w.userId === userId)) {
              act.workers = act.workers.filter((w) => w.userId !== userId);
            }
          }
        }
        // Standalone activities — workers list also gets stripped (both modes).
        for (const act of next.standaloneActivities) {
          if (act.workers.some((w) => w.userId === userId)) {
            act.workers = act.workers.filter((w) => w.userId !== userId);
          }
        }
        // NOTE on staff `allocationCount`: the rail's staff array is
        // DERIVED at the page level in `useDispatchBoardDay` via
        // `mapStaff(staffQuery.data, crews)`. Patching crews + workers
        // here (above) is enough — `mapStaff` recomputes the per-user
        // allocation count from the patched crews on the next render,
        // so the ⊘ button + × N badge disappear immediately without
        // any additional fix.
        qc.setQueryData(key, next);
      }
      return { prev };
    },
    onError: (_e, _v, ctx) => {
      if (ctx?.prev) qc.setQueryData(dispatchKeys.day(date), ctx.prev);
    },
    onSettled: () => {
      qc.invalidateQueries({ queryKey: dispatchKeys.day(date) });
    },
  });
}

/* -------------------------------------------------------------------------- */
/* End-of-day Capture + Roll-over (Phase 7g — mock-first)                     */
/* -------------------------------------------------------------------------- */

/**
 * Day-wide capture status snapshot. Drives the "Day Capture" summary
 * sheet (Phase 7g-M2). Re-fetches whenever a capture or roll-over lands
 * via cache invalidation in the mutation hooks below.
 *
 * `enabled` should be the modal-open flag: the summary is gated on the hub
 * being open AND re-fetched every time it opens (`staleTime: 0`). This is
 * deliberate — assigning/creating/moving an activity updates the board
 * `day` query but does NOT invalidate this key, so without the
 * fetch-on-open the hub would show a stale list missing activities the
 * dispatcher just put on a crew. Opening the hub = a fresh API call.
 */
export function useCaptureSummary(date: string, enabled = true) {
  return useQuery({
    queryKey: dispatchKeys.captureSummary(date),
    queryFn: () => dispatchApi.getCaptureSummary(date),
    enabled: enabled && Boolean(date),
    staleTime: 0,
    refetchOnMount: 'always',
  });
}

/**
 * Create a backlog task on the board (Sprint A). Not optimistic — the
 * created task is tenant-wide and surfaces in EVERY day's rail, so we
 * invalidate the current day (and rely on refetch-on-navigate for others)
 * rather than hand-patch every cached day. Mock-only; 7g-REAL maps to
 * POST /WorkOrderTask.
 */
export function useCreateBacklogTask(date: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (payload: BacklogTaskCreatePayload) =>
      dispatchApi.createBacklogTask(payload),
    onSettled: () => {
      qc.invalidateQueries({ queryKey: dispatchKeys.all });
    },
  });
}

/**
 * Apply a capture to an activity. Optimistically marks the activity as
 * captured + writes the per-task/per-asset entries straight into the day
 * cache, so the Day Capture summary updates instantly. Settle triggers a
 * full refetch of both the day view and the capture summary.
 */
export function useCaptureActivity(date: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (payload: CaptureActivityPayload) =>
      dispatchApi.captureActivity(date, payload),
    onMutate: async (payload) => {
      const dayKey = dispatchKeys.day(date);
      await qc.cancelQueries({ queryKey: dayKey });
      const prev = qc.getQueryData<DayCacheShape>(dayKey);
      if (prev) {
        const next = structuredClone(prev);
        const activity = findActivityInState(next, payload.activityId);
        if (activity) {
          // Mirror mock-store.captureActivity logic — same fields, same rules.

          // Attach-at-capture (staged backlog tasks) — JOB-MATCH guard +
          // JOB-INHERIT, row born already-captured, id consumed.
          for (const at of payload.attachTasks ?? []) {
            const u = at.task;
            if (next.consumedUnassignedIds.includes(u.id)) continue;
            if (
              activity.parentJobNumber &&
              u.jobNumber &&
              u.jobNumber !== activity.parentJobNumber
            ) {
              continue;
            }
            if (!activity.parentJobNumber && u.jobNumber) {
              activity.parentJobId = u.jobId || activity.parentJobId;
              activity.parentJobNumber = u.jobNumber;
            }
            activity.tasks.push({
              taskId: `t-${Date.now()}-${Math.random().toString(36).slice(2, 5)}`,
              workOrderTaskId: u.id,
              jobNumber: u.jobNumber,
              jobName: u.jobName,
              description: u.title,
              type: u.type,
              taskTypeRaw: u.taskType,
              scaffoldCode: u.scaffoldCode,
              clientName: u.clientName,
              siteName: u.siteName,
              // Mirror mock-store — preserve board-created charge/billable.
              chargeUpType: u.chargeUpType,
              isBillable: u.isBillable,
              executionStatus: at.status,
              perWorkerHours: at.perWorkerHours,
              captureNotes: at.notes?.trim() || undefined,
            });
            next.consumedUnassignedIds.push(u.id);
          }

          // Ad-hoc-at-capture (staged new tasks) — isAdhoc + explicit
          // charge type, General task type.
          for (const ah of payload.adhocTasks ?? []) {
            const desc = ah.description.trim();
            if (!desc) continue;
            const gen = `${Date.now()}-${Math.random().toString(36).slice(2, 5)}`;
            // Keep in lock-step with mock-store.captureActivity's typeFor.
            const adhocType: DispatchJobType =
              ah.taskType === 1 ? 'ERECT'
              : ah.taskType === 2 ? 'MODIFY'
              : ah.taskType === 3 ? 'DISMANTLE'
              : ah.taskType === 4 ? 'INSPECT'
              : ah.taskType === 5 ? 'HANDOVER'
              : 'OTHER';
            activity.tasks.push({
              taskId: `t-${gen}`,
              workOrderTaskId: `adhoc-${gen}`,
              jobNumber: activity.parentJobNumber ?? '',
              jobName: '',
              description: desc,
              type: adhocType,
              taskTypeRaw: ah.taskType,
              scaffoldCode: ah.scaffoldCode,
              adhocWorkOrderRef: ah.workOrderRef,
              partialStrikePercent: ah.partialStrikePercent,
              clientName: activity.clientName,
              siteName: activity.siteName,
              executionStatus: ah.status,
              perWorkerHours: ah.perWorkerHours,
              captureNotes: ah.notes?.trim() || undefined,
              isAdhoc: true,
              chargeUpType: ah.chargeUpType,
              isBillable: ah.isBillable ? undefined : false,
            });
          }

          if (payload.travelHours !== undefined)
            activity.travelHours = payload.travelHours;
          if (payload.loadingHours !== undefined)
            activity.loadingHours = payload.loadingHours;
          if (payload.standbyHours !== undefined)
            activity.standbyHours = payload.standbyHours;

          const taskById = new Map(activity.tasks.map((t) => [t.taskId, t]));
          for (const tc of payload.taskCaptures) {
            const task = taskById.get(tc.taskId);
            if (!task) continue;
            task.executionStatus = tc.status;
            if (tc.perWorkerHours !== undefined)
              task.perWorkerHours = tc.perWorkerHours;
            if (tc.notes !== undefined)
              task.captureNotes = tc.notes.trim() || undefined;
          }

          if (activity.vehicles) {
            // Match by `rowId ?? assetId` — see mock-store.captureActivity
            // for why: mock vehicles have no rowId, so assetId is the
            // stable fallback key. Must stay in lock-step with the store.
            const vehById = new Map(
              activity.vehicles.map((v) => [v.rowId ?? v.assetId, v]),
            );
            for (const ac of payload.assetCaptures) {
              const veh = vehById.get(ac.assetRowId);
              if (!veh) continue;
              if (ac.km !== undefined) veh.km = ac.km;
              if (ac.usageHours !== undefined) veh.usageHours = ac.usageHours;
              veh.isRoundTrip = ac.isRoundTrip;
              if (ac.notes !== undefined)
                veh.captureNotes = ac.notes.trim() || undefined;
            }
          }

          activity.isCaptured = true;
          activity.capturedAt = new Date().toISOString();
        }
        qc.setQueryData(dayKey, next);
      }
      return { prev };
    },
    onError: (_e, _v, ctx) => {
      if (ctx?.prev) qc.setQueryData(dispatchKeys.day(date), ctx.prev);
    },
    onSettled: (_data, _err, payload) => {
      qc.invalidateQueries({ queryKey: dispatchKeys.day(date) });
      qc.invalidateQueries({ queryKey: dispatchKeys.captureSummary(date) });
      // G2: capture cascades ScaffoldTask → Scaffold → WorkOrder → Job
      // statuses on the backend (G1). Invalidate those feature caches so the
      // Scaffold / Work Order / Job views reflect the advanced statuses
      // without a manual refresh.
      qc.invalidateQueries({ queryKey: ['scaffolds'] });
      qc.invalidateQueries({ queryKey: ['work-orders'] });
      qc.invalidateQueries({ queryKey: ['jobs'] });
      // COMPLIANCE CHAIN — captured Inspections may have spawned next
      // visits on other days; invalidate those so the new activity shows
      // when the dispatcher navigates there.
      const chainDates = new Set<string>(
        [
          ...payload.taskCaptures.map((t) => t.nextInspectionDate),
          ...(payload.attachTasks ?? []).map((t) => t.nextInspectionDate),
          ...(payload.adhocTasks ?? []).map((t) => t.nextInspectionDate),
        ].filter((d): d is string => Boolean(d) && d !== date),
      );
      for (const cd of chainDates) {
        qc.invalidateQueries({ queryKey: dispatchKeys.day(cd) });
        qc.invalidateQueries({ queryKey: dispatchKeys.captureSummary(cd) });
      }
    },
  });
}

/**
 * Day-level cartage capture (vehicle km per day, allocated per WorkOrder).
 *
 * NOT optimistic — cartage jobs live in their own ledger that the board
 * `day` query doesn't surface, so there's nothing in the cache to patch.
 * On success we invalidate the day + capture-summary (the summary's km
 * roll-up may shift) and the downstream feature caches that read cartage,
 * mirroring `useCaptureActivity`'s settle-time invalidation. Returns the
 * created/updated cartage-job count so the page's toast can report it.
 */
export function useCaptureDayCartage(date: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (req: CaptureDayCartageRequest) =>
      dispatchApi.captureDayCartage(req),
    onSettled: () => {
      qc.invalidateQueries({ queryKey: dispatchKeys.day(date) });
      qc.invalidateQueries({ queryKey: dispatchKeys.captureSummary(date) });
      // Cartage feeds invoicing (InvoiceGenerationService reads the splits),
      // so refresh the scaffold / work-order / job views whose cost roll-ups
      // change — same downstream set as the activity-capture mutation.
      qc.invalidateQueries({ queryKey: ['scaffolds'] });
      qc.invalidateQueries({ queryKey: ['work-orders'] });
      qc.invalidateQueries({ queryKey: ['jobs'] });
    },
  });
}

/**
 * Roll over incomplete tasks onto target dates. NOT optimistic — the
 * source activity loses tasks AND new activities appear on possibly
 * different days. Doing all of that in one optimistic patch is risky
 * (multi-day cache reads) — we let `onSettled` invalidate every touched
 * date so refetch shows the authoritative state.
 *
 * Returns the set of touched dates so the toast can summarise what
 * happened (e.g. "Rolled 2 tasks to 2026-06-11 and 1 to 2026-06-12").
 */
export function useRollOverActivity(date: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (payload: RollOverPayload) =>
      dispatchApi.rollOverActivity(date, payload),
    onSettled: (_data, _err, payload) => {
      qc.invalidateQueries({ queryKey: dispatchKeys.day(date) });
      qc.invalidateQueries({ queryKey: dispatchKeys.captureSummary(date) });
      // Invalidate every target day's caches so the new continuation
      // activities surface when the user navigates there.
      const targetDates = new Set(payload.tasks.map((t) => t.targetDate));
      for (const td of targetDates) {
        if (td !== date) {
          qc.invalidateQueries({ queryKey: dispatchKeys.day(td) });
          qc.invalidateQueries({ queryKey: dispatchKeys.captureSummary(td) });
        }
      }
    },
  });
}
